<template>
  <div>
    <PTransparentLoader :loading="loading">
      <CRow>
        <CCol lg="12">
          <div class="d-flex justify-content-end mb-4">
            <CButton
              color="primary"
              variant="outline"
              @click="$router.push({name: 'adminPayoutList'})"
            >
              <CIcon name="cipArrowLeft" size="sm" class="mr-1"/>
              <translate translate-context="admin-payout">Back to list</translate>
            </CButton>
          </div>

          <!-- Payout Details Card -->
          <CCard v-if="payout" class="mb-4">
            <CCardHeader>
              <h5 class="mb-0">
                <translate translate-context="admin-payout">Payout Information</translate>
              </h5>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md="6">
                  <div class="mb-3">
                    <label class="font-weight-bold">
                      <translate translate-context="admin-payout">Status</translate>:
                    </label>
                    <CBadge :color="getStatusColor(payout.status)" class="badge-outline ml-2">
                      <div class="d-flex align-items-center">
                        <CIcon :name="getStatusIcon(payout.status)" size="custom-size" :height="8" class="mr-2"/>
                        <span class="font-weight-normal">{{ getStatusLabel(payout.status) }}</span>
                      </div>
                    </CBadge>
                  </div>
                  
                  <div class="mb-3">
                    <label class="font-weight-bold">
                      <translate translate-context="admin-payout">Amount</translate>:
                    </label>
                    <span class="ml-2">{{ payout.amount | currency(payout.currency) }}</span>
                  </div>
                  
                  <div class="mb-3">
                    <label class="font-weight-bold">
                      <translate translate-context="admin-payout">Request Date</translate>:
                    </label>
                    <span class="ml-2">{{ payout.requested_at | dateTime }}</span>
                  </div>
                  
                  <div class="mb-3" v-if="payout.paid_at">
                    <label class="font-weight-bold">
                      <translate translate-context="admin-payout">Payment Date</translate>:
                    </label>
                    <span class="ml-2">{{ payout.paid_at | dateTime }}</span>
                  </div>
                </CCol>
                
                <CCol md="6">
                  <div class="mb-3">
                    <label class="font-weight-bold">
                      <translate translate-context="admin-payout">Contract</translate>:
                    </label>
                    <span class="ml-2">{{ payout.contract_name || '-' }}</span>
                  </div>
                  
                  <div class="mb-3">
                    <label class="font-weight-bold">
                      <translate translate-context="admin-payout">Contract ID</translate>:
                    </label>
                    <span class="ml-2">{{ payout.contract_id || '-' }}</span>
                  </div>
                  
                  <div class="mb-3" v-if="payout.invoice_url">
                    <label class="font-weight-bold">
                      <translate translate-context="admin-payout">Invoice</translate>:
                    </label>
                    <CLink :href="payout.invoice_url" target="_blank" class="ml-2">
                      <CIcon name="cipDownload" size="sm" class="mr-1"/>
                      <translate translate-context="admin-payout">Download</translate>
                    </CLink>
                  </div>
                  
                  <div class="mb-3" v-if="payout.notes">
                    <label class="font-weight-bold">
                      <translate translate-context="admin-payout">Notes</translate>:
                    </label>
                    <p class="ml-2 mb-0">{{ payout.notes }}</p>
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>

          <!-- User Details Card -->
          <CCard v-if="payout" class="mb-4">
            <CCardHeader>
              <h5 class="mb-0">
                <translate translate-context="admin-payout">User Information</translate>
              </h5>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md="6">
                  <div class="mb-3">
                    <label class="font-weight-bold">
                      <translate translate-context="admin-payout">User Email</translate>:
                    </label>
                    <span class="ml-2">
                      {{ payout.user_email }}
                      <CButton
                        color="primary"
                        size="sm"
                        variant="ghost"
                        @click="copyEmail(payout.user_email)"
                        class="ml-2 p-1"
                        v-c-tooltip="$pgettext('admin-payout', 'Copy email')"
                      >
                        <CIcon name="cipCopy" size="sm"/>
                      </CButton>
                    </span>
                  </div>
                  
                  <div class="mb-3" v-if="payout.user_name">
                    <label class="font-weight-bold">
                      <translate translate-context="admin-payout">User Name</translate>:
                    </label>
                    <span class="ml-2">{{ payout.user_name }}</span>
                  </div>
                  
                  <div class="mb-3" v-if="payout.user_id">
                    <label class="font-weight-bold">
                      <translate translate-context="admin-payout">User ID</translate>:
                    </label>
                    <span class="ml-2">{{ payout.user_id }}</span>
                  </div>
                </CCol>
                
                <CCol md="6">
                  <div class="mb-3" v-if="payout.bank_account">
                    <label class="font-weight-bold">
                      <translate translate-context="admin-payout">Bank Account</translate>:
                    </label>
                    <span class="ml-2">{{ payout.bank_account }}</span>
                  </div>
                  
                  <div class="mb-3" v-if="payout.iban">
                    <label class="font-weight-bold">
                      <translate translate-context="admin-payout">IBAN</translate>:
                    </label>
                    <span class="ml-2">{{ payout.iban }}</span>
                  </div>
                  
                  <div class="mb-3" v-if="payout.swift">
                    <label class="font-weight-bold">
                      <translate translate-context="admin-payout">SWIFT</translate>:
                    </label>
                    <span class="ml-2">{{ payout.swift }}</span>
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>

          <!-- Actions Card -->
          <CCard v-if="payout" class="mb-4">
            <CCardBody>
              <div class="d-flex justify-content-between">
                <div>
                  <CButton
                    v-if="canMarkPaid && payout.status === 'pending'"
                    color="success"
                    size="lg"
                    @click="handleMarkPayoutAsPaid()"
                    class="mr-2"
                  >
                    <CIcon name="cipCheck" size="lg" class="mr-1"/>
                    <translate translate-context="admin-payout">Mark as Paid</translate>
                  </CButton>
                  
                  <CButton
                    v-if="payout.status === 'paid'"
                    color="info"
                    size="lg"
                    variant="outline"
                    @click="downloadCSV()"
                    class="mr-2"
                  >
                    <CIcon name="cipDownload" size="lg" class="mr-1"/>
                    <translate translate-context="admin-payout">Download Excel</translate>
                  </CButton>
                </div>
                
                <CButton
                  color="danger"
                  size="lg"
                  variant="ghost"
                  @click="$router.push({name: 'adminPayoutList'})"
                >
                  <translate translate-context="admin-payout">Cancel</translate>
                </CButton>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </PTransparentLoader>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import { PAYOUT_STATUS, PAYOUT_STATUS_LABELS, PAYOUT_STATUS_COLORS } from './constants'

export default {
  name: 'AdminPayoutDetail',
  components: {
    PTransparentLoader
  },
  data() {
    return {
      loading: false
    }
  },
  computed: {
    ...mapGetters('adminPayout', ['payout']),
    ...mapGetters('login', ['hasAnyPerms']),
    canMarkPaid() {
      return this.hasAnyPerms(['admin.payout.mark_paid'])
    }
  },
  methods: {
    ...mapActions('adminPayout', ['fetchAdminPayoutDetail', 'markPayoutAsPaid', 'downloadPayoutExcel']),
    ...mapActions('login', ['fetchConfig']),
    
    getStatusLabel(status) {
      return PAYOUT_STATUS_LABELS[status] || status
    },
    
    getStatusColor(status) {
      return PAYOUT_STATUS_COLORS[status] || 'light'
    },
    
    getStatusIcon(status) {
      return status === PAYOUT_STATUS.PAID ? 'cipDotGreen' : 'cipDotOrange'
    },
    
    async handleMarkPayoutAsPaid() {
      if (!confirm(this.$pgettext('admin-payout', 'Are you sure you want to mark this payout as paid?'))) {
        return
      }
      
      try {
        await this.markPayoutAsPaid({id: this.$route.params.id, data: {}})
        await this.fetchAdminPayoutDetail(this.$route.params.id)
        this.notifySuccess(this.$pgettext('admin-payout', 'Payout marked as paid successfully'))
      } catch (error) {
        this.notifyError(this.$pgettext('admin-payout', 'Error marking payout as paid'))
      }
    },
    
    async downloadCSV() {
      try {
        await this.downloadPayoutExcel(this.$route.params.id)
        this.notifySuccess(this.$pgettext('admin-payout', 'Excel downloaded successfully'))
      } catch (error) {
        this.notifyError(this.$pgettext('admin-payout', 'Error downloading Excel'))
      }
    },
    
    copyEmail(email) {
      navigator.clipboard.writeText(email).then(() => {
        this.notifySuccess(this.$pgettext('admin-payout', 'Email copied to clipboard'))
      }).catch(() => {
        this.notifyError(this.$pgettext('admin-payout', 'Failed to copy email'))
      })
    }
  },
  async mounted() {
    // Fetch admin payout config for permissions
    await this.fetchConfig({type: 'admin_payout'})
    
    // Load payout details
    this.loading = true
    try {
      await this.fetchAdminPayoutDetail(this.$route.params.id)
    } catch (error) {
      this.notifyError(this.$pgettext('admin-payout', 'Error loading payout details'))
      this.$router.push({name: 'adminPayoutList'})
    } finally {
      this.loading = false
    }
  }
}
</script>